import { makeAutoObservable } from 'mobx';
import { protectedContentApiClient } from '../../api/api-fetcher/api-client';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { IErrorObject } from '../../utils/services/error-parsing-service';

interface IPermissions {
  podiumEnabled: boolean;
  rolePermissionsEnabled: boolean;
}

export enum Permissions {
  PresenterSlides = 'Presenter Slides',
  Polls = 'Polls',
  Messages = 'Messages',
  Attendees = 'Attendees',
  QandA = 'Q&A',
  PresenterQuestions = 'Presenter Questions',
  Disconnect = 'Disconnect'
}

interface IPermission {
  active: boolean;
  name: string;
  parentId: number | null;
  settingId: number;
  stringValue: string;
  value: boolean;
}

interface IPermissionsDTO {
  accessCode: string;
  id: number;
  label: string;
  permissions: IPermission[];
  podiumLinkId: string;
  presentationId: number;
  presenterLink: string;
}

class PermissionsStore {
  private podiumMessagesEnabled: boolean;
  private podiumSlidesEnabled: boolean;
  private podiumPollsEnabled: boolean | null;
  private podiumAttendeesEnabled: boolean | null;
  private podiumEnabled: boolean | null;
  private podiumQandaEnabled: boolean | null;
  private podiumPresenterQuestionsEnabled: boolean;
  private podiumDisconnectEnabled: boolean | null;
  private isLoadingPermissions: boolean;
  private isRolePermissionEnabled: boolean | null;

  get isLoading (): boolean {
    return this.isLoadingPermissions;
  }

  constructor (
        private readonly presenterEmbedId: string,
        private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
        private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore()
  ) {
    makeAutoObservable(this);
    this.podiumEnabled = null;
    this.podiumMessagesEnabled = false;
    this.podiumPollsEnabled = null;
    this.podiumSlidesEnabled = false;
    this.podiumAttendeesEnabled = null;
    this.podiumQandaEnabled = null;
    this.podiumPresenterQuestionsEnabled = false;
    this.podiumDisconnectEnabled = null;
    this.isLoadingPermissions = true;
    this.isRolePermissionEnabled = null;
  }

  public getPermissions = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();

    if (!presentationId) return;

    const relevantPath = `presenter/presentation/${presentationId}/podiumlink`;
    try {
      const permissionPath = `presenter/presentation/${presentationId}/permissible`;
      const { podiumEnabled, rolePermissionsEnabled } = await protectedContentApiClient.get<IPermissions>(permissionPath);
      this.podiumEnabled = podiumEnabled;
      this.isRolePermissionEnabled = rolePermissionsEnabled;

      const result = await protectedContentApiClient.get<IPermissionsDTO[]>(relevantPath);
      const permissionSet = result.find(p => p.podiumLinkId === this.presenterEmbedId);
      this.deserialize(permissionSet?.permissions || result[0].permissions);
    } catch (e) {
      const error = e as IErrorObject;
      this.errorBoundary.setErrorMessage(error);
    } finally {
      this.isLoadingPermissions = false;
    }
  };

  private getPermissionByName = (permissions: IPermission[], name: Permissions): boolean => {
    const permission = permissions.filter(item => item.name === name);
    const { value } = permission[0];

    return value;
  };

  private deserialize = (fromDTO: IPermission[]) => {
    this.podiumSlidesEnabled = this.getPermissionByName(fromDTO, Permissions.PresenterSlides);
    this.podiumPollsEnabled = this.getPermissionByName(fromDTO, Permissions.Polls);
    this.podiumMessagesEnabled = this.getPermissionByName(fromDTO, Permissions.Messages);
    this.podiumAttendeesEnabled = this.getPermissionByName(fromDTO, Permissions.Attendees);
    this.podiumQandaEnabled = this.getPermissionByName(fromDTO, Permissions.QandA);
    this.podiumDisconnectEnabled = this.getPermissionByName(fromDTO, Permissions.Disconnect);
    this.podiumPresenterQuestionsEnabled = this.isRolePermissionEnabled
      ? this.getPermissionByName(fromDTO, Permissions.PresenterQuestions)
      : this.getPermissionByName(fromDTO, Permissions.QandA);

  };

  public isPodiumPresenterQuestionsEnabled = (): boolean => {
    return this.podiumPresenterQuestionsEnabled;
  };

  public isPodiumMessagesEnabled = ():boolean => {
    return this.podiumMessagesEnabled;
  };

  public isPodiumPollsEnabled = ():boolean | null => {
    return this.podiumPollsEnabled;
  };

  public isPodiumSlidesEnabled= ():boolean => {
    return this.podiumSlidesEnabled;
  };

  public isPodiumAttendeesEnabled = (): boolean | null => {
    return this.podiumAttendeesEnabled;
  };

  public isPodiumEnabled = (): boolean | null => {
    return this.podiumEnabled;
  };

  public isPodiumQandaEnabled = (): boolean | null => {
    return this.podiumQandaEnabled;
  };

  public isPodiumDisconnectEnabled = (): boolean | null => {
    return this.podiumDisconnectEnabled;
  };
}

export type TPermissionsStore = PermissionsStore;

export default PermissionsStore;
